import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FieldRequiredIndicator, ValidationError } from '../../components';
import { shouldShowFieldError } from '../../util/formFieldError';
import css from './CustomExtendedDataField.module.css';
import CustomFieldFileDisplay from './CustomFieldFileDisplay';

// Accept PDF and JPG files
const ACCEPT_FILES = '.pdf,.jpg,.jpeg';

const hasUploadedFile = value =>
  !!value && (typeof value === 'object' ? !!value.fileUrl : value.length > 0);

const CustomFieldFileUpload = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    form,
    showRequiredIndicator,
    submitAttempted,
  } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = fieldConfig?.saveConfig?.label || fieldConfig?.label;
  const showRequired = showRequiredIndicator ?? isRequired;
  const validateMaybe = isRequired
    ? {
        validate: value =>
          !hasUploadedFile(value) && (requiredMessage || defaultRequiredMessage),
      }
    : {};

  const handleFileUpload = async (file) => {
    if (!file) return;

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload to S3 via our server endpoint
      const apiServerPort = process.env.REACT_APP_DEV_API_SERVER_PORT || '3500';
      const apiBaseUrl = process.env.NODE_ENV === 'development' 
        ? `http://localhost:${apiServerPort}` 
        : '';
      
      const response = await fetch(`${apiBaseUrl}/api/files/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Include cookies for authentication
        // Note: Don't set Content-Type header, let the browser set it with boundary
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || `Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Store file information in the form
        const fileInfo = {
          name: result.fileName,
          size: result.fileSize,
          type: result.fileType,
          fileUrl: result.fileUrl,
          fileKey: result.fileKey,
          uploadedAt: new Date().toISOString()
        };
        
        form.change(name, fileInfo);
        form.blur(name);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Show error message to the user in a more user-friendly way
      const errorMessage = error.message.includes('permission denied') 
        ? 'File upload failed due to server permissions. Please try again or contact support.'
        : `File upload failed: ${error.message}`;
      alert(errorMessage);
    }
  };

  return (
    <Field
      accept={ACCEPT_FILES}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      type="file"
      form={null}
      {...validateMaybe}
    >
      {fieldProps => {
        const { accept, id, input, label, meta } = fieldProps;
        const { name: inputName, type, value } = input;
        const onChange = e => {
          const file = e.target.files[0];
          if (file) {
            handleFileUpload(file);
          }
        };

        const showError = shouldShowFieldError(meta, submitAttempted);
        const hasFile = value && value.fileUrl;

        const handleDelete = (fieldName) => {
          form.change(fieldName, null);
          form.blur(fieldName);
        };

        return (
          <div className={css.customField}>
            <div className={css.uploadFileWrapper}>
              <label
                className={classNames(css.label, { [css.labelError]: showError })}
                htmlFor={id}
              >
                {label}
                {showRequired ? <FieldRequiredIndicator /> : null}
              </label>
              <input
                accept={accept}
                id={id}
                name={inputName}
                className={css.uploadFileInput}
                onChange={onChange}
                type={type}
              />
              {placeholderMessage && !hasFile && (
                <div className={css.placeholder}>
                  {placeholderMessage}
                </div>
              )}
              {hasFile && (
                <CustomFieldFileDisplay
                  fileInfo={value}
                  onDelete={handleDelete}
                  fieldName={name}
                />
              )}
              <ValidationError fieldMeta={{ touched: showError, error: meta.error }} />
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export default CustomFieldFileUpload; 